



























import { extend } from 'vee-validate';
import {
  required,
  min,
  confirmed,
} from 'vee-validate/dist/rules';
import { SfTabs } from '@storefront-ui/vue';
import { defineComponent, useContext, computed } from '@nuxtjs/composition-api';
import { useUser } from '@wemade-vsf/customer';
import { customerPasswordRegExp, invalidPasswordMsg } from '@wemade-vsf/customer/lib/runtime/helpers/passwordValidation';
import PasswordResetForm from 'pages/customer/MyAccount/MyProfile/PasswordResetForm.vue';
import type { OnFormComplete, OnFormError, SubmitEventPayload } from '@wemade-vsf/customer/lib/runtime/types/form';
import type { ProfileUpdateFormFields, PasswordResetFormFields } from 'pages/customer/MyAccount/MyProfile/types';

extend('required', {
  ...required,
  message: 'This field is required',
});

extend('min', {
  ...min,
  message: 'The field should have at least {length} characters',
});

extend('password', {
  message: invalidPasswordMsg,
  validate: (value: string) => customerPasswordRegExp.test(value),
});

extend('confirmed', {
  ...confirmed,
  message: 'Passwords don\'t match',
});

export default defineComponent({
  name: 'MyProfile',
  components: {
    SfTabs,
    ProfileUpdateForm: () => import('pages/customer/MyAccount/MyProfile/ProfileUpdateForm.vue'),
    ProfileDetails: () => import('pages/customer/MyAccount/MyProfile/ProfileDetails.vue'),
    PasswordResetForm,
  },

  setup() {
    const {
      changePassword,
      loading,
      updateUser,
      error,
      isCompanyCustomer
    } = useUser();

    const { app, i18n } = useContext();
    const canUpdateProfile = computed(() => isCompanyCustomer ? app.$wm.company.canEditAccountInfo : true);

    const formHandler = async <T extends () => Promise<unknown>>(
      onSubmit: T,
      onComplete: OnFormComplete,
      onError: OnFormError,
    ) => {
      await onSubmit();
      const actionErr = error.value.changePassword || error.value.updateUser;
      if (actionErr) {
        onError(actionErr.toString());
      } else {
        onComplete();
      }
    };

    const updatePersonalData = ({
      form,
      onComplete,
      onError,
    } : SubmitEventPayload<ProfileUpdateFormFields>) => formHandler(
      async () => updateUser({ user: form }),
      onComplete,
      onError,
    );

    const updatePassword = ({
      form,
      onComplete,
      onError,
    } : SubmitEventPayload<PasswordResetFormFields>) => formHandler(
      async () => changePassword({ current: form.currentPassword, new: form.newPassword }),
      onComplete,
      onError,
    );

    const privacyPolicy = app.$wm?.theme?.pages?.privacyPolicy?.[i18n.locale] || '';

    return {
      loading,
      updatePersonalData,
      updatePassword,
      canUpdateProfile,
      privacyPolicy
    };
  },
});
